import React, { useEffect, useState } from "react";
import { AssessmentMessage } from "../Health Assessment/AssesmentMessage";
import TimelineItem from "./TimelineItem";
import WellnessTimeline from "./WellnessTimeline";
import DisplayItems from "./DisplayItems";
import { myResultUrl } from "../../utils/constants";
import useApis from "../../services/useAPI";
import { useParams } from "react-router-dom";
import { steps } from "../Dashboard/Dashboard";
import { Alert, Spinner } from "react-bootstrap";
import { Button } from "../../stories/Buttons/Button";
import AppointmentDetails from "../Dashboard/AppointmentDetails";
import Markdown from "react-markdown";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { ErrorDisplay } from "../Errors/ErrorDisplay";
import { formatTrackingNumber } from "../../utils/utils";

/**
 * TestKitDetails component fetches and displays detailed information about a specific test kit.
 * It shows information such as test kit status, assessment details, and provides navigation
 * to the assessment or timeline of the test.
 *
 * @returns {JSX.Element} The rendered TestKitDetails component.
 */

const TestKitDetails = () => {
  const params = useParams();
  const auth = useAuth();
  const { test_kit_id } = params;
  const { callApi, resultGet, isLoading, serverError } = useApis();
  const [testFlag, setTestFlag] = useState(1);
  const [showButton, setShowButton] = useState(false);
  const [error, setError] = useState("");
  const [utcDateTime] = useState("");

  const meetingDetails = (time) => {
    if (time !== undefined || (utcDateTime !== undefined && utcDateTime !== "")) {
      const dateObj = time ? new Date(time) : new Date(utcDateTime);
      const userDateTime = dateObj.toLocaleString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      const _24BeforeMeeting = new Date(dateObj.getTime() - 24 * 60 * 60 * 1000);
      const currentTime = new Date();

      setShowButton(currentTime < _24BeforeMeeting);

      return userDateTime;
    }
  };

  // Fetches test kit details
  useEffect(() => {
    callApi(null, `${myResultUrl}${test_kit_id}/`, "GET");
  }, [auth?.editReload]);

  useEffect(() => {
    if (resultGet?.data?.test_kit_type?.includes("dna-") || resultGet?.data?.test_kit_type?.includes("boots-dna-")) {
      setTestFlag(0);
    }
  }, [resultGet?.data?.test_kit_type]);

  useEffect(() => {
    localStorage.setItem('isVenousBlood',resultGet?.data?.bid !== null)
  }, [resultGet?.data?.bid]);

  useEffect(() => {
    setError(serverError);
  }, [serverError])

  if ((!resultGet) && (!error)) {
    return (
      <div className="text-center">
        <Spinner animation="border" role='output' className="my-custom-spinner"/>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} consent={false} />;
  }


  const renderErrorAlert = () => {
    if (!selectedTest.archive_reason_message && !selectedTest.error_note) return null;

    const message = selectedTest.error_note || selectedTest.archive_reason_message;

    return (
        selectedTest?.bid ? null : (
          <Alert
            className="rounded dashboard-alert"
            variant="danger"
            dismissible={false}
          >
            <Markdown>{message}</Markdown>
          </Alert>
        )
    );
  };

  // const renderInstructions = () => {
  //   let nextStep = steps[currentStepIndex] || { title: () => null };

  //   // If next step is 'assessment, skip to another step
  //   if (nextStep.name === "assessment") {
  //     nextStep = steps[currentStepIndex + 2] || { title: () => null };
  //   }

    // If it is Menopause or PCOS test AND assessment wasn't filled
    // AND still can be filled - then show warning
    // if (
    //   (selectedTest?.test_kit_type === "blood-menopause" ||
    //     selectedTest?.test_kit_type === "blood-polycystic") && !assessment.hasFilledAssessment &&
    //   assessment.canEditAssessment
    // ) {
    //   return (
    //     <div>
    //       <p className="text-justify">
    //         Our records show that we have not received your completed Health Assessment for <strong>{selectedTest?.test_kit_type_name}</strong>, and without this, you will not receive your results.
    //       </p>
    //       <p className="text-justify">
    //         The information you submit will provide health and lifestyle context for the doctor who reviews your results. We will also ask you questions about your menstrual cycle because blood results can depend on the phase of a woman’s cycle.
    //       </p>
    //     </div>
    //   );
    // }

  //   if (!nextStep.instructions) return null;

  //   return nextStep.instructions({
  //     test_id: selectedTest?.id,
  //     bid: selectedTest?.bid,
  //     viewInstructionsUrl,
  //     filledAssessment: !!assessment.hasFilledAssessment,
  //     errorReason: selectedTest?.archive_reason,
  //     errorReasonMessage: selectedTest?.archive_reason_message,
  //     consultation_status: selectedTest?.consultation_status,
  //   });
  // };

  const selectedTest = resultGet?.data;
  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner animation="border"  className="my-custom-spinner"/>
      </div>
    );
  }

  if (!selectedTest) return <div>No test details available.</div>;

  // Displaying tracking number in a formatted way
  const selectedTestProcessed = {
    ...selectedTest,
    postage_tracking: formatTrackingNumber(selectedTest?.postage_tracking),
  };

  const fieldsArray = Object.keys(getFields(selectedTestProcessed)).map((key) => ({
    name: key,
    ...getFields(selectedTestProcessed)[key],
  }));
  const assessment = {
    requireAssessment: selectedTest?.test_kit_type?.startsWith('blood-') || selectedTest?.test_kit_type?.startsWith('boots-blood-'),
    hasFilledAssessment: selectedTest?.assessment_id,
    canEditAssessment: selectedTest?.can_edit_assessment,
  };

  const anyStepFailed = steps.some((step) =>
    step.failed?.(selectedTest?.test_kit_status, selectedTest?.archive_reason)
  );

  const currentStep = steps.find((step) =>
    step.inProgress(selectedTest?.test_kit_status, selectedTest?.archive_reason)
  );
  const currentStepIndex = steps.findIndex((step) => step === currentStep);
  let viewInstructionsUrl = "https://myhealthchecked.com/help/videos/blood";
  const isSexualHealthTest = selectedTest?.test_kit_type?.indexOf("sex-health") !== -1;
  if (isSexualHealthTest) {
    viewInstructionsUrl = "https://myhealthchecked.com/help/videos/sexual-health";
  }
  const reportLink = `/my-reports/blood/${selectedTest?.report_id || ""}`;
  const reportReady = selectedTest?.test_kit_status === "report-ready";

  return (
    <div className="row justify-content-center dashboard-page test-kit-details">
      <div className="col-md-12">
        <div className="dashboard">
          <h1 className="mb-4 mt-3 fs-24 text-teal test-kit-detail-title">{(selectedTest.test_kit_type_name)}</h1>
          {renderErrorAlert()}
          {/* <div className="mb-4">
            {!!reportReady && (
              <p>
                Our doctor has reviewed your results. Your report has been published and is ready to view.
              </p>
            )}
            <div className="d-flex my-test-buttons">
              {!!reportReady && (
                <Button
                  className="me-3"
                  href={reportLink}
                  color="primary"
                  label="View report"
                />
              )}
              {testFlag === 1 &&
                assessment.hasFilledAssessment &&
                !assessment.canEditAssessment && (
                  <Button
                    className="view-health-assessment-btn"
                    href={`/assessment-details/${selectedTest?.id}`}
                    color="primary"
                    variant="outline-primary"
                    label="View health assessment"
                  />
                )}
            </div>
          </div> */}
          {testFlag === 1 && (
            <>
              <AppointmentDetails
                {...selectedTest}
                testId={selectedTest?.id}
                calendlyStatus={selectedTest?.calendly_status}
                unconfirmedResultMessage={selectedTest?.unconfirmed_result_message}
                meetingDetails={meetingDetails}
                showButton={showButton}
              />
              {/* <TimelineItem
                test={{ ...selectedTest }}
                steps={steps}
                assessment={assessment}
                hasFilledAssessment={!!assessment.hasFilledAssessment}
                canEditAssessment={!!assessment?.canEditAssessment}
                currentStepIndex={currentStepIndex}
                anyStepFailed={anyStepFailed}
              /> */}
            </>
          )}
          {testFlag === 0 && <WellnessTimeline test={{ ...selectedTest }} />}

          {selectedTest?.test_kit_type?.includes("blood-") && (
            <AssessmentMessage
              assessment={assessment}
              status={selectedTest?.test_kit_status}
              test_id={selectedTest?.id}
              testFlag={testFlag}
              selectedTest={selectedTest}
              reportReady={reportReady}
              reportLink={reportLink}
              bId={selectedTest.bid}
            />
          )}
          <h2 className="py-2 text-teal ageo-semibold fs-24">Test summary</h2>
          <DisplayItems fields={fieldsArray} data={selectedTestProcessed} />
        </div>
      </div>
    </div>
  );
};

export default TestKitDetails;

const getFields = (selectedTest) => {
  if (selectedTest?.test_kit_type?.startsWith("dna") || selectedTest?.test_kit_type?.startsWith("boots-dna-")) {
    return {
      primary_booking_reference: { title: "Booking Reference", fmt: "inline-code" },
      primary_barcode: { title: "Barcode", fmt: "inline-code" },
      test_kit_type_name: { title: "Test Kit Type", fmt: "as-title" },
      test_kit_status_name: { title: "Status", fmt: "as-title" },
      postage_tracking: { title: "Tracking number", fmt: "inline-code" },
    };
  } else {
    return {
      test_kit_type_name: { title: "Test Kit Type", fmt: "as-title" },
      test_kit_status_name: { title: "Status", fmt: "as-title" },
      sex: { title: "Gender at Birth", fmt: "as-title" },
      dob: { title: "Date of Birth", fmt: "date" },
      primary_barcode: { fmt: "inline-code" },
      secondary_barcode: { fmt: "inline-code" },
      postage_tracking: { title: "Tracking number", fmt: "inline-code" },
    };
  }
};